import { useCallback, useEffect, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import {
  loadOllieFindings,
  markFindingSeen,
  removeFinding,
} from 'src/features/OllieSlice'
import useOllieVenueId from 'src/hooks/useOllieVenueId'
import { postOllieFindingInteraction } from 'src/utils/ollieFindingsApi'
import {
  listNewOllieFindings,
  listOllieFindingsForChecks,
  listVisibleOllieFindings,
  selectOllieFindingsState,
} from 'src/utils/ollieFindingsSelectors'

/**
 * This person's Ollie findings for the venue in the URL. One shared read in
 * Redux (fresh for 5 minutes), so the panel, button dot and feature pages
 * don't each hit Express.
 *
 * @param {{ autoLoad?: boolean, checkIds?: string[] }} [options]
 */
const useOllieFindings = ({ autoLoad = true, checkIds = null } = {}) => {
  const dispatch = useDispatch()
  const { venueId } = useOllieVenueId()
  const findings = useSelector(selectOllieFindingsState)

  const reload = useCallback(
    ({ force = false } = {}) =>
      venueId ? dispatch(loadOllieFindings({ venueId, force })) : null,
    [dispatch, venueId],
  )

  useEffect(() => {
    if (autoLoad && venueId) reload()
  }, [autoLoad, venueId, reload])

  const record = useCallback(
    async (finding, interaction, surface) => {
      if (!venueId || !finding) return null
      try {
        return await postOllieFindingInteraction(venueId, {
          finding,
          interaction,
          surface,
        })
      } catch (error) {
        return { success: false, message: error?.message }
      }
    },
    [venueId],
  )

  /** Hide straight away; Express keeps it hidden until something new appears. */
  const putAway = useCallback(
    async (finding, interaction, surface) => {
      dispatch(removeFinding({ checkId: finding.checkId }))
      return record(finding, interaction, surface)
    },
    [dispatch, record],
  )

  /** Undo a snooze: Express clears this person's cool-off, then re-read so it's back everywhere. */
  const unsnooze = useCallback(
    async (finding, surface) => {
      const outcome = await record(finding, 'unsnoozed', surface)
      if (outcome && outcome.success !== false) reload({ force: true })
      return outcome
    },
    [record, reload],
  )

  const markShown = useCallback(
    (finding, surface) => {
      if (finding?.checkId) {
        dispatch(markFindingSeen({ checkId: finding.checkId }))
      }
      return record(finding, 'shown', surface)
    },
    [dispatch, record],
  )

  const checkKey = checkIds ? checkIds.join('|') : ''
  const forChecks = useMemo(
    () =>
      checkKey
        ? listOllieFindingsForChecks(findings, venueId, checkKey.split('|'))
        : [],
    [findings, venueId, checkKey],
  )
  // Stable arrays: pages feed these into effects that set parent state.
  const visibleFindings = useMemo(
    () => listVisibleOllieFindings(findings, venueId),
    [findings, venueId],
  )
  const newFindings = useMemo(
    () => listNewOllieFindings(findings, venueId),
    [findings, venueId],
  )

  return {
    venueId,
    status: findings?.venueId === venueId ? findings.status : 'idle',
    error: findings?.venueId === venueId ? findings.error : null,
    asOf: findings?.venueId === venueId ? findings.asOf : null,
    stale: Boolean(findings?.venueId === venueId && findings.stale),
    quiet: findings?.venueId === venueId ? findings.quiet : true,
    findings: visibleFindings,
    newFindings,
    forChecks,
    reload,
    markShown,
    markClicked: (finding, surface) => record(finding, 'clicked', surface),
    snooze: (finding, surface) => putAway(finding, 'snoozed', surface),
    unsnooze,
    dismiss: (finding, surface) => putAway(finding, 'dismissed', surface),
  }
}

export default useOllieFindings
